/**
 * What stepping onto a tile would mean, asked before doing it.
 *
 * The interface needs three answers the command pipeline only gives by refusing: is
 * this an attack, at what odds, and would it start a war. Asking the same functions
 * the pipeline uses — rather than the shell keeping its own idea of what an attack is —
 * is what keeps the odds on screen equal to the odds rolled.
 */

import { mayEnter } from '../diplomacy/borders.js';
import type { Content } from '../../../content/schema/index.js';
import { cityAt } from '../cities/index.js';
import { atWar, truceLeft } from '../diplomacy/index.js';
import { NONE, type GameState, type Id, type Unit } from '../state/types.js';
import { unitIndex, unitsAtIndex } from '../units/index.js';
import { feelsZoc, zocBlocks, zocPlane } from '../units/zoc.js';
import { canAttack, matchup, type Matchup } from './index.js';

export type MovePreview =
  | { kind: 'move' }
  /** Someone at peace with you is there; attacking means declaring war on them first. */
  | { kind: 'war'; civs: Id[]; truce: number }
  | { kind: 'attack'; matchup: Matchup }
  /** Nothing there can defend: walking in takes it. */
  | { kind: 'capture'; city: boolean; units: number }
  /** Something hostile is there and this unit cannot fight it. */
  | { kind: 'blocked' }
  /** An enemy's zone of control forbids this step. */
  | { kind: 'zoc' }
  /** A rival's land, closed at peace without open borders (ADR-0060). */
  | { kind: 'border'; civ: Id };

export function previewMove(state: GameState, content: Content, unit: Unit, to: number): MovePreview {
  const others = unitsAtIndex(state, to).filter((u) => u.owner !== unit.owner);
  const standing = cityAt(state, state.map, to);
  const city = standing && standing.owner !== unit.owner ? standing : null;
  const def0 = content.units.get(unit.type);
  const zoc = (): boolean =>
    feelsZoc(def0) &&
    unit.aboard === NONE &&
    zocBlocks(state, content, unit.owner, zocPlane(state, content, unit.owner), unitIndex(state.map, unit), to);
  if (others.length === 0 && !city) {
    const at = state.map.owner[to]!;
    if (!mayEnter(state, unit.owner, to)) return { kind: 'border', civ: at };
    return zoc() ? { kind: 'zoc' } : { kind: 'move' };
  }

  const owners = new Set(others.map((u) => u.owner));
  if (city) owners.add(city.owner);
  const peaceful = [...owners].filter((owner) => !atWar(state, unit.owner, owner)).sort((a, b) => a - b);
  const def = content.units.get(unit.type);
  if (peaceful.length > 0) {
    if (!canAttack(def)) return { kind: 'blocked' };
    const truce = Math.max(...peaceful.map((civ) => truceLeft(state, unit.owner, civ)));
    return { kind: 'war', civs: peaceful, truce };
  }
  if (!canAttack(def)) return { kind: 'blocked' };
  const m = matchup(state, content, unit, to);
  if (m) return { kind: 'attack', matchup: m };
  if (zoc()) return { kind: 'zoc' };
  return { kind: 'capture', city: city !== null, units: others.length };
}
